/**
 * Agent Settings Store
 *
 * Generic Zustand store for per-agent configuration, keyed by agentType.
 * Used by the intelligence agents (cassandra, compass, keystone).
 * DB-first: every write goes through agent_settings before local state updates.
 */

import { create } from 'zustand';
import {
  getAgentSettings,
  upsertAgentSettings,
  type AgentSettings,
  type AgentTemplateSettings,
} from '../../lib/agentSettingsService';
import { getAgentDefinition, isCustomAgentType } from '../../lib/ai/agentDefinitions';
import { DEFAULT_MODEL } from '../../../supabase/functions/_shared/modelRequest.ts';

// ── Defaults ──────────────────────────────────────────────────────────────────

export const DEFAULT_AGENT_SETTINGS: Omit<AgentSettings, 'workspaceId' | 'agentType' | 'updatedAt'> = {
  enabled: true,
  model: DEFAULT_MODEL,
  systemPrompt: null,
  autoRunOnCreate: false,
};

// ── State ─────────────────────────────────────────────────────────────────────

export interface AgentSettingsUpdate extends AgentTemplateSettings {
  enabled?: boolean;
  model?: string;
  systemPrompt?: string | null;
  autoRunOnCreate?: boolean;
}

interface AgentSlice {
  config: AgentSettings | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  loadedWorkspaceId: string | null;
}

function emptySlice(): AgentSlice {
  return { config: null, isLoading: false, isSaving: false, error: null, loadedWorkspaceId: null };
}

export interface AgentSettingsStoreState {
  byAgent: Record<string, AgentSlice>;

  /** Load config for (workspace, agentType). Falls back to defaults when unset. */
  fetchConfig: (workspaceId: string, agentType: string) => Promise<void>;

  /** Persist a config update for (workspace, agentType). */
  saveConfig: (
    workspaceId: string,
    agentType: string,
    updates: AgentSettingsUpdate,
    updatedBy?: string,
  ) => Promise<{ success: boolean; error?: string }>;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useAgentSettingsStore = create<AgentSettingsStoreState>((set, get) => ({
  byAgent: {},

  fetchConfig: async (workspaceId, agentType) => {
    const existing = get().byAgent[agentType];
    if (existing?.loadedWorkspaceId === workspaceId && existing.config !== null) return;

    set((s) => ({
      byAgent: { ...s.byAgent, [agentType]: { ...(s.byAgent[agentType] ?? emptySlice()), isLoading: true, error: null } },
    }));

    const { data, error } = await getAgentSettings(workspaceId, agentType);
    if (error) {
      set((s) => ({
        byAgent: { ...s.byAgent, [agentType]: { ...(s.byAgent[agentType] ?? emptySlice()), isLoading: false, error } },
      }));
      return;
    }

    // An agent on the template ships off (spec §2 L: off is absence); a missing row is not "on".
    const templateAgent = !!getAgentDefinition(agentType) || isCustomAgentType(agentType);
    const effective: AgentSettings = data ?? {
      workspaceId,
      agentType,
      ...DEFAULT_AGENT_SETTINGS,
      ...(templateAgent ? { enabled: false, model: getAgentDefinition(agentType)?.defaultModel ?? DEFAULT_AGENT_SETTINGS.model } : {}),
      updatedAt: new Date().toISOString(),
    };

    set((s) => ({
      byAgent: {
        ...s.byAgent,
        [agentType]: { ...(s.byAgent[agentType] ?? emptySlice()), config: effective, isLoading: false, loadedWorkspaceId: workspaceId },
      },
    }));
  },

  saveConfig: async (workspaceId, agentType, updates, updatedBy) => {
    const current = get().byAgent[agentType]?.config;
    const base: Omit<AgentSettings, 'workspaceId' | 'agentType' | 'updatedAt'> = current
      ? {
          enabled:         current.enabled,
          model:           current.model,
          systemPrompt:    current.systemPrompt ?? null,
          autoRunOnCreate: current.autoRunOnCreate,
        }
      : DEFAULT_AGENT_SETTINGS;
    // Template switches travel only when the caller changes them (null = keep, on the RPC).

    const next = { ...base, ...updates };

    set((s) => ({
      byAgent: { ...s.byAgent, [agentType]: { ...(s.byAgent[agentType] ?? emptySlice()), isSaving: true, error: null } },
    }));

    const { data, error } = await upsertAgentSettings({ workspaceId, agentType, ...next, updatedBy });

    if (error || !data) {
      set((s) => ({
        byAgent: {
          ...s.byAgent,
          [agentType]: { ...(s.byAgent[agentType] ?? emptySlice()), isSaving: false, error: error ?? 'Save failed' },
        },
      }));
      return { success: false, error: error ?? 'Save failed' };
    }

    set((s) => ({
      byAgent: {
        ...s.byAgent,
        [agentType]: { ...(s.byAgent[agentType] ?? emptySlice()), config: data, isSaving: false, loadedWorkspaceId: workspaceId },
      },
    }));
    return { success: true };
  },
}));
